import React, { useEffect, useState } from 'react';
import { X, Download, Share2 } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export default function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isAppInstalled);

    // Check if it's an iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the prompt after a delay to avoid interrupting the user
      setTimeout(() => setShowPrompt(true), 2000);
    };

    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
      // Store in localStorage that the app was installed
      localStorage.setItem('appInstalled', 'true');
    };

    // Check if we should show the prompt (not shown in last 7 days)
    const lastPrompt = localStorage.getItem('lastInstallPrompt');
    const shouldShowPrompt = !lastPrompt || 
      (Date.now() - parseInt(lastPrompt, 10)) > 7 * 24 * 60 * 60 * 1000;

    if (shouldShowPrompt && !isAppInstalled) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);
    }

    window.addEventListener('appinstalled', handleAppInstalled);

    // Clean up
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setDeferredPrompt(null);
        setShowPrompt(false);
      } else {
        console.log('User dismissed the install prompt');
        // Store the last prompt time
        localStorage.setItem('lastInstallPrompt', Date.now().toString());
      }
    } catch (err) {
      console.error('Error during installation:', err);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store the last prompt time
    localStorage.setItem('lastInstallPrompt', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900">Install App</h3>
          {isIOS ? (
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                To install this app on your iPhone:
              </p>
              <ol className="mt-2 text-sm text-gray-500 list-decimal list-inside space-y-1">
                <li>Tap the <Share2 className="inline-block h-4 w-4 mx-1" /> share button</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
              </ol>
            </div>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              Install this application on your device for quick and easy access.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            {!isIOS && (
              <button
                onClick={handleInstall}
                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Download className="inline-block h-4 w-4 mr-2" />
                Install
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-4 text-gray-400 hover:text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}